import { apiClient } from '@/lib/api-client';
import {
  TreinamentoCategoria,
  TreinamentoCurso,
  TreinamentoModulo,
  TreinamentoAula,
  TreinamentoCertificado,
  TreinamentoRelatorio,
  TreinamentoAulaProgresso,
} from '@/types';

function apiBaseUrl(): string {
  if (typeof window !== 'undefined' && (window as any).__ENV__?.VITE_API_URL) {
    return (window as any).__ENV__.VITE_API_URL;
  }
  return import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
}

export const treinamentoCategoriaService = {
  list: () => apiClient.get<TreinamentoCategoria[]>('/treinamentos/categorias'),
  create: (data: { nome: string; ordem?: number; global?: boolean; empresa_id?: string }) =>
    apiClient.post<TreinamentoCategoria>('/treinamentos/categorias', data),
  update: (id: string, data: { nome?: string; ordem?: number }) =>
    apiClient.put<TreinamentoCategoria>(`/treinamentos/categorias/${id}`, data),
  remove: (id: string) => apiClient.delete<void>(`/treinamentos/categorias/${id}`),
};

export const treinamentoCursoService = {
  catalogo: () => apiClient.get<TreinamentoCategoria[]>('/treinamentos/catalogo'),
  getById: (id: string) => apiClient.get<TreinamentoCurso>(`/treinamentos/cursos/${id}`),
  create: (data: {
    categoria_id: string;
    titulo: string;
    descricao?: string;
    status?: string;
    ordem?: number;
  }) => apiClient.post<TreinamentoCurso>('/treinamentos/cursos', data),
  update: (id: string, data: Partial<TreinamentoCurso>) =>
    apiClient.put<TreinamentoCurso>(`/treinamentos/cursos/${id}`, data),
  remove: (id: string) => apiClient.delete<void>(`/treinamentos/cursos/${id}`),
  capaUrl: (id: string) => apiClient.get<{ url: string }>(`/treinamentos/cursos/${id}/capa`),
  uploadCapa: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('capa', file);
    return apiClient.postMultipart<TreinamentoCurso>(`/treinamentos/cursos/${id}/capa`, fd);
  },
  relatorio: (id: string) => apiClient.get<TreinamentoRelatorio>(`/treinamentos/cursos/${id}/relatorio`),
  reordenarModulos: (cursoId: string, ordem: string[]) =>
    apiClient.put<{ ok: boolean }>(`/treinamentos/cursos/${cursoId}/modulos/reordenar`, { ordem }),
};

export const treinamentoModuloService = {
  create: (cursoId: string, data: { titulo: string; ordem?: number }) =>
    apiClient.post<TreinamentoModulo>(`/treinamentos/cursos/${cursoId}/modulos`, data),
  update: (id: string, data: { titulo?: string; ordem?: number }) =>
    apiClient.put<TreinamentoModulo>(`/treinamentos/modulos/${id}`, data),
  remove: (id: string) => apiClient.delete<void>(`/treinamentos/modulos/${id}`),
  reordenarAulas: (moduloId: string, ordem: string[]) =>
    apiClient.put<{ ok: boolean }>(`/treinamentos/modulos/${moduloId}/aulas/reordenar`, { ordem }),
};

export const treinamentoAulaService = {
  create: (moduloId: string, data: { titulo: string; descricao?: string; ordem?: number }) =>
    apiClient.post<TreinamentoAula>(`/treinamentos/modulos/${moduloId}/aulas`, data),
  update: (id: string, data: { titulo?: string; descricao?: string; ordem?: number; duracao_segundos?: number }) =>
    apiClient.put<TreinamentoAula>(`/treinamentos/aulas/${id}`, data),
  remove: (id: string) => apiClient.delete<void>(`/treinamentos/aulas/${id}`),
  playUrl: (id: string) => apiClient.get<{ url: string }>(`/treinamentos/aulas/${id}/play`),
  salvarProgresso: (id: string, data: { posicao_segundos: number; concluido?: boolean }) =>
    apiClient.put<{ progresso: TreinamentoAulaProgresso; certificado: TreinamentoCertificado | null }>(
      `/treinamentos/aulas/${id}/progresso`,
      data,
    ),
  concluir: (id: string) =>
    apiClient.post<{ progresso: TreinamentoAulaProgresso; certificado: TreinamentoCertificado | null }>(
      `/treinamentos/aulas/${id}/concluir`,
    ),

  /** Upload de vídeo com progresso (XHR — fetch não expõe upload progress). */
  uploadVideo: (
    aulaId: string,
    file: File,
    onProgress?: (percent: number) => void,
    duracaoSegundos?: number,
    onRetry?: (tentativa: number, maxTentativas: number) => void,
  ): Promise<TreinamentoAula> => {
    return uploadVideoComRetry(aulaId, file, onProgress, duracaoSegundos, onRetry);
  },
};

const UPLOAD_TIMEOUT_MS = 15 * 60 * 1000; // vídeo grande pode demorar bem mais que o default do browser
const UPLOAD_RETRY_DELAYS_MS = [2000, 5000]; // delay antes de cada retry
const UPLOAD_MAX_TENTATIVAS = UPLOAD_RETRY_DELAYS_MS.length + 1; // 1 tentativa inicial + retries

type ResultadoUpload =
  | { ok: true; aula: TreinamentoAula }
  | { ok: false; retryable: boolean; erro: Error };

/** Erro de rede/timeout/5xx — vale tentar de novo. Erro HTTP 4xx (validação, permissão) não. */
function tentarUploadVideo(
  aulaId: string,
  file: File,
  onProgress: ((percent: number) => void) | undefined,
  duracaoSegundos: number | undefined,
): Promise<ResultadoUpload> {
  return new Promise((resolve) => {
    const fd = new FormData();
    fd.append('video', file);
    if (duracaoSegundos) fd.append('duracao_segundos', String(Math.round(duracaoSegundos)));

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBaseUrl()}/treinamentos/aulas/${aulaId}/video`);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    const token = localStorage.getItem('okr_auth_token');
    const empresa = localStorage.getItem('okr_empresa_ativa');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (empresa) xhr.setRequestHeader('X-Empresa-ID', empresa);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve({ ok: true, aula: JSON.parse(xhr.responseText) });
        } catch {
          resolve({ ok: false, retryable: false, erro: new Error('Resposta inválida do servidor') });
        }
      } else {
        let msg = `Erro HTTP ${xhr.status}`;
        try {
          msg = JSON.parse(xhr.responseText).error || msg;
        } catch {
          /* resposta não-JSON */
        }
        // 5xx é falha do servidor/proxy, tipicamente transitória — vale retentar.
        // 4xx é erro de validação/permissão — retentar não muda o resultado.
        resolve({ ok: false, retryable: xhr.status >= 500, erro: new Error(msg) });
      }
    };
    xhr.onerror = () => resolve({ ok: false, retryable: true, erro: new Error('Falha de conexão no upload') });
    xhr.ontimeout = () =>
      resolve({ ok: false, retryable: true, erro: new Error('Tempo esgotado no upload — verifique sua conexão') });
    xhr.send(fd);
  });
}

async function uploadVideoComRetry(
  aulaId: string,
  file: File,
  onProgress: ((percent: number) => void) | undefined,
  duracaoSegundos: number | undefined,
  onRetry: ((tentativa: number, maxTentativas: number) => void) | undefined,
): Promise<TreinamentoAula> {
  let ultimoErro = new Error('Erro desconhecido no upload');
  for (let tentativa = 1; tentativa <= UPLOAD_MAX_TENTATIVAS; tentativa++) {
    const resultado = await tentarUploadVideo(aulaId, file, onProgress, duracaoSegundos);
    if (resultado.ok === true) return resultado.aula;

    ultimoErro = resultado.erro;
    const haMaisTentativas = tentativa < UPLOAD_MAX_TENTATIVAS;
    if (!resultado.retryable || !haMaisTentativas) break;

    onRetry?.(tentativa + 1, UPLOAD_MAX_TENTATIVAS);
    await new Promise((r) => setTimeout(r, UPLOAD_RETRY_DELAYS_MS[tentativa - 1]));
  }
  throw ultimoErro;
}
